import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Typography,
} from '@mui/material'

import { LinearProgressWithLabel } from '@/components'
import { useAppContext } from '@/hooks'

import {
	scriptGenerationActions,
	ScriptGenerationPhase,
} from './scriptGenerationActions'

type ScriptGenerationDialogProps = {
	description: string
	envName?: string
	errorMessage?: string | null
	onCancel: () => void
	onClose: () => void
	onDeploy: () => void
	onDownload: () => void
	phase: Exclude<ScriptGenerationPhase, 'config'>
	progress: number
}

export const ScriptGenerationDialog = ({
	description,
	envName,
	errorMessage,
	onCancel,
	onClose,
	onDeploy,
	onDownload,
	phase,
	progress,
}: ScriptGenerationDialogProps) => {
	const { t } = useAppContext()
	const actions = scriptGenerationActions(phase)
	const message =
		phase === 'deployed'
			? t('DEPLOYMENT_MARK_CREATED')
			: errorMessage || description

	return (
		<Dialog
			open
			onClose={actions.cancel ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			data-testid="deployment-package-generate-dialog"
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle sx={{ color: '#1F4954', fontSize: 16, fontWeight: 700 }}>
				{t('DEPLOYMENT_PACKAGES_CREATE_SCRIPTS')}
				{envName ? `: ${envName}` : ''}
			</DialogTitle>
			<DialogContent>
				<Typography
					sx={{
						color: errorMessage && phase !== 'deployed' ? '#C62828' : '#3D3D3D',
						fontSize: 13,
						lineHeight: 1.5,
						mb: actions.showProgress ? 2 : 0,
					}}
				>
					{message}
				</Typography>
				{actions.showProgress && progress > 0 ? (
					<LinearProgressWithLabel value={progress} />
				) : null}
				{actions.showProgress && progress <= 0 ? <LinearProgress /> : null}
			</DialogContent>
			<DialogActions sx={{ pb: 2, px: 3 }}>
				{actions.download ? (
					<Button
						variant="contained"
						disableElevation
						onClick={onDownload}
						data-testid="deployment-package-generate-download"
						sx={{ textTransform: 'none' }}
					>
						{t('DOWNLOAD')}
					</Button>
				) : null}
				{actions.markDeployed ? (
					<Button
						variant="outlined"
						onClick={onDeploy}
						data-testid="deployment-package-generate-deploy"
						sx={{ textTransform: 'none' }}
					>
						{t('MARK_AS_DEPLOYED')}
					</Button>
				) : null}
				{actions.cancel ? (
					<Button
						onClick={onCancel}
						data-testid="deployment-package-generate-cancel"
						sx={{ textTransform: 'none' }}
					>
						{t('CANCEL')}
					</Button>
				) : null}
				{actions.close ? (
					<Button
						onClick={onClose}
						data-testid="deployment-package-generate-close"
						sx={{ textTransform: 'none' }}
					>
						{t('CLOSE')}
					</Button>
				) : null}
			</DialogActions>
		</Dialog>
	)
}
